/* ============================================================================
   18 · New Customer Landing — the first visit, told in order
   Figma 9Dhvc9N1v5FWO9b54mpCkN · LP 3468:242337 (D 3311:134400 · M 3311:134404)
   ----------------------------------------------------------------------------
   What's being decided here:

     • A hero whose copy gets out of the way. Arc'teryx Sperro SV, measured:
       the copy fades linearly over the first 150px of scroll and comes back
       on the way up; at desktop it holds still while the photo scrolls out
       from under it, at mobile it rides up with the photo.
     • The first-order offer as a fixed pill (Favorites Confirmation Modal
       3333:161227): it rides with the masthead, then pins under the compact
       bar, darkening off the hero so it stays legible, until it's closed.
     • One Women / Men state across Our Catalog, the Core Line and Define
       Your Uniform (gender.js), with real wearfigs.com content for both.
     • The Core Line as tabs with a hover ghost (core.js), the fabric story
       as sticky tabs over looping plates (fabric.js), the colour rail on
       Our Catalog's component (Core / Limited Edition), and the uniform
       table that recolours and quick-buys (dyu.js).

   Motion is the Homepage / PLP / PDP vocabulary (mo.js, motion.dev): the
   first screen rises 16 on --mo-slow while the photo settles from 1.04,
   every section below reveals row by row, drawers are the PDP's, and the
   spring is spent only on add-to-bag. Reduced motion keeps every fade and
   drops the travel; nothing is ever left hidden.
   ========================================================================== */

import { mountFigsNav, openFigsAccount } from '/src/figs-nav/figs-nav.js';
import {
  animate, inView, hover, stagger, MO, state, tween, play, track, teardown, setSpeed, clearTracked, $, $$, mqDesktop, revealOnView,
} from '../10-pdp-prototype/mo.js';
import { DATA, colorsFor } from './data.js';
import { gender, onGender, mountToggles } from './gender.js';
import { initCore } from './core.js';
import { initFabric, fabricSync } from './fabric.js';
import { initDyu } from './dyu.js';

/* ============================================================
   HEADER — the shared nav, once, ink on the hero (index.html
   positions it over the photo)
   ============================================================ */
mountFigsNav({
  promo: $('[data-fnav-promo]'),
  masthead: $('[data-fnav-masthead]'),
  theme: 'light',
});
/* the compact bar's height while it shows — sticky columns park under it */
(function initCompactOffset() {
  const compactBar = document.getElementById('fnav-compact-bar');
  const masthead = document.getElementById('fnav-masthead');
  if (!compactBar || !masthead) return;
  let showing = false;
  const sync = () => document.documentElement.style.setProperty('--fnav-compact-offset', showing ? `${compactBar.offsetHeight}px` : '0px');
  new IntersectionObserver(([e]) => { showing = !e.isIntersecting; sync(); }, { threshold: 0 }).observe(masthead);
  addEventListener('resize', sync);
  sync();
})();

/* ============================================================
   PROMO PILL — rides with the masthead, then holds 12px under
   the compact bar (or the viewport top), every scroll frame
   ============================================================ */
function initPromo() {
  const pill = $('[data-promo]');
  const hero = $('[data-hero]');
  const masthead = document.getElementById('fnav-masthead');
  const compactBar = document.getElementById('fnav-compact-bar');
  let raf = 0;
  const place = () => {
    raf = 0;
    if (pill.hidden) return;
    const desktop = mqDesktop.matches;
    /* Figma: D 120 from the page top, M 92 (the masthead's foot) */
    const base = desktop ? 120 - scrollY : (masthead ? masthead.getBoundingClientRect().bottom : 92 - scrollY);
    let pin = 12;
    if (compactBar && compactBar.style.pointerEvents === 'auto') pin = Math.max(12, compactBar.getBoundingClientRect().bottom + 12);
    const top = Math.max(pin, base);
    pill.style.setProperty('--promo-top', `${Math.round(top)}px`);
    const onHero = hero.getBoundingClientRect().bottom > top + pill.offsetHeight;
    pill.dataset.onLight = String(!onHero);
    /* sticky columns (the fabric copy) hold below the pinned pill */
    document.documentElement.style.setProperty('--ncl-promo-clear', `${Math.round(top + pill.offsetHeight + 12)}px`);
  };
  const queue = () => { if (!raf) raf = requestAnimationFrame(place); };
  addEventListener('scroll', queue, { passive: true });
  addEventListener('resize', queue);
  /* the compact bar animates in and out on its own clock — follow it */
  if (compactBar) new MutationObserver(() => { queue(); setTimeout(queue, 420); }).observe(compactBar, { attributes: true, attributeFilter: ['style'] });
  place();

  $('[data-promo-close]', pill).addEventListener('click', async () => {
    await play(pill, { opacity: [1, 0], y: [0, -8] }, { duration: MO.base, ease: MO.eExit })?.finished;
    pill.hidden = true;
    pill.style.opacity = '';
    document.documentElement.style.setProperty('--ncl-promo-clear', '0px');
  });
  /* the offer is the email sign-up: the shared account drawer's Sign up */
  $('[data-promo-offer]', pill).addEventListener('click', () => openFigsAccount('signup'));
}

/* ============================================================
   HERO — the copy fades over the first 150px of scroll
   (Arc'teryx), holding still at desktop while the photo moves
   ============================================================ */
function initHeroScroll() {
  const content = $('.ncl-hero__content');
  let raf = 0;
  const update = () => {
    raf = 0;
    const y = Math.max(0, scrollY);
    const t = Math.min(1, y / 150);
    content.style.opacity = String(1 - t);
    content.style.transform = mqDesktop.matches && !state.reduced ? `translate3d(0, ${Math.min(y, 150)}px, 0)` : '';
    content.style.visibility = t >= 1 ? 'hidden' : '';
  };
  addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
  mqDesktop.addEventListener('change', update);
  update();
}

/* ============================================================
   OUR CATALOG — six tiles per gender; a dragging rail at 1440
   ============================================================ */
const catalogRail = $('[data-catalog-rail]');
const crop = (c) => (c ? ` style="position:absolute;max-width:none;width:${c.w}%;height:${c.h}%;left:${c.l}%;top:${c.t}%"` : '');
/* the Homepage rail's hover (08 initCollections): the plate pushes in to
   --mo-scale-in on --mo-slow, the label lifts 4 on --mo-base; the label's
   underline reveal is CSS */
const tileHovers = new Map();
function bindTileHover(rail) {
  (tileHovers.get(rail) || []).forEach((off) => off?.());
  const offs = [];
  $$('[data-ctile]', rail).forEach((t) => {
    const plate = $('.ncl-ctile__img', t);
    const label = $('.ncl-ctile__label', t);
    offs.push(hover(t, () => {
      track(animate(plate, { scale: MO.scaleIn }, { duration: MO.slow, ease: MO.eEntrance }));
      track(animate(label, { y: -4 }, { duration: MO.base, ease: MO.eEntrance }));
      return () => {
        track(animate(plate, { scale: 1 }, { duration: MO.slow, ease: MO.eEntrance }));
        track(animate(label, { y: 0 }, { duration: MO.base, ease: MO.eEntrance }));
      };
    }));
  });
  tileHovers.set(rail, offs);
}
/* one tile markup for both rails (Our Catalog, Color Is Everything We Do) */
const tileHTML = (t) => {
  const ext = /^https?:/.test(t.href);
  return `
      <a class="ncl-ctile${t.tone === 'dark' ? ' ncl-ctile--dark' : ''}" href="${t.href}"${ext ? ' target="_blank" rel="noopener"' : ''} data-ctile draggable="false">
        <img class="ncl-ctile__img" src="${t.image}" alt="" loading="lazy" decoding="async" draggable="false"${crop(t.crop)}>
        <span class="ncl-ctile__label">${t.label}</span>
      </a>`;
};
function renderCatalog(g) {
  catalogRail.innerHTML = DATA[g].catalog.map(tileHTML).join('');
  catalogRail.scrollLeft = 0;
  bindTileHover(catalogRail);
}
function initCatalog() {
  renderCatalog(gender());
  onGender((g, source) => {
    const touched = source === $('[data-catalog]');
    if (!touched) { renderCatalog(g); settle($('[data-catalog]'), '[data-ctile]'); return; }
    play($$('[data-ctile]', catalogRail), { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished.then(() => {
      renderCatalog(g);
      tween($$('[data-ctile]', catalogRail), { opacity: [0, 1], y: [MO.riseSm, 0] },
        { duration: MO.slow, ease: MO.eEntrance, delay: (i) => i * MO.stagTight });
    });
  });
}

/* ============================================================
   COLOR IS EVERYTHING WE DO — Core / Limited Edition, six
   tiles each, on the catalog's rail. The toggle swaps the line
   the way the gender toggle swaps the catalog; a gender change
   elsewhere only re-points the links.
   ============================================================ */
const colorsRail = $('[data-colors-rail]');
let colorLine = 'core';
function renderColors() {
  colorsRail.innerHTML = colorsFor(colorLine, gender()).map(tileHTML).join('');
  colorsRail.scrollLeft = 0;
  bindTileHover(colorsRail);
}
function initColors() {
  renderColors();
  const btns = $$('[data-color-line]');
  btns.forEach((b) => b.addEventListener('click', () => {
    const line = b.dataset.colorLine;
    if (line === colorLine) return;
    colorLine = line;
    btns.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    play($$('[data-ctile]', colorsRail), { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished.then(() => {
      renderColors();
      tween($$('[data-ctile]', colorsRail), { opacity: [0, 1], y: [MO.riseSm, 0] },
        { duration: MO.slow, ease: MO.eEntrance, delay: (i) => i * MO.stagTight });
    });
  }));
  onGender(() => {
    $$('[data-ctile]', colorsRail).forEach((a, i) => {
      const c = colorsFor(colorLine, gender())[i];
      if (!c) return;
      a.href = c.href;
      if (/^https?:/.test(c.href)) { a.target = '_blank'; a.rel = 'noopener'; } else { a.removeAttribute('target'); a.removeAttribute('rel'); }
    });
  });
}

/* a section re-rendered by a toggle somewhere else: if it has already
   revealed, its new content just shows */
function settle(section, sel) {
  if (!section.hasAttribute('data-revealed')) { armReveals(); return; }
  $$(sel, section).forEach((el) => { el.style.opacity = '1'; });
}

/* ============================================================
   CAMPAIGN — the still drifts with the scroll (parallax near);
   the corner control holds it
   ============================================================ */
function initCampaign() {
  const sec = $('[data-campaign]');
  const media = $('[data-campaign-media]', sec);
  const btn = $('[data-campaign-toggle]', sec);
  let held = false;
  let raf = 0;
  const update = () => {
    raf = 0;
    if (held || state.reduced) { media.style.transform = ''; return; }
    const r = sec.getBoundingClientRect();
    const p = (r.top + r.height / 2 - innerHeight / 2) / (innerHeight + r.height);
    /* --mo-parallax-mid (0.16) of the section's height, at most ±8% */
    media.style.transform = `translate3d(0, ${(Math.max(-0.5, Math.min(0.5, p)) * r.height * 0.16).toFixed(2)}px, 0)`;
  };
  addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
  btn.addEventListener('click', () => {
    held = !held;
    btn.setAttribute('aria-pressed', String(held));
    btn.setAttribute('aria-label', held ? 'Play the campaign motion' : 'Pause the campaign motion');
    update();
  });
  update();
}

/* ============================================================
   MOTION — first-screen intro + scroll reveals
   ============================================================ */
function initIntro() {
  tween($('[data-hero-media]'), { scale: [MO.scaleIn, 1] }, { duration: MO.cinematic, ease: MO.eEntrance });
  tween([$('[data-hero-eyebrow]'), $('[data-hero-title]'), $('[data-hero-body]')], { opacity: [0, 1], y: [MO.riseSm, 0] },
    { duration: MO.slow, ease: MO.eEntrance, delay: (i) => 0.18 + i * MO.stagBase });
  tween($('[data-promo]'), { opacity: [0, 1], y: [-8, 0] }, { duration: MO.slow, ease: MO.eEntrance, delay: 0.7 });
}

const REVEALS = [
  ['[data-reveal]', { opacity: [0, 1], y: [MO.riseSm, 0] }, {}],
  ['[data-reveal-lines]', { opacity: [0, 1], y: [MO.riseMd, 0] }, { duration: MO.deliberate }],
  ['[data-reveal-plate]', { opacity: [0, 1], scale: [MO.scaleIn, 1] }, { duration: MO.deliberate }],
  ['[data-vp]', { opacity: [0, 1], x: [-MO.riseXs, 0] }, { stag: MO.stagBase }],
  ['[data-core-item], [data-core-reveal]', { opacity: [0, 1], y: [MO.riseSm, 0] }, {}],
  ['[data-fabric-reveal]', { opacity: [0, 1], y: [MO.riseSm, 0] }, {}],
  ['[data-dyu] [data-slot]', { opacity: [0, 1], y: [MO.riseSm, 0] }, { stag: 0.02 }],
  ['[data-footer-item]', { opacity: [0, 1], y: [MO.riseMd, 0] }, {}],
];
const armed = new WeakSet();
function armReveals() {
  REVEALS.forEach(([sel, kf, o]) => {
    const els = $$(sel).filter((el) => !armed.has(el) && !el.closest('[data-hero]'));
    if (!els.length) return;
    els.forEach((el) => armed.add(el));
    revealOnView(els, kf, { duration: o.duration || MO.slow, stag: o.stag || MO.stagTight });
  });
  /* Our Catalog and the colour rail enter like the Homepage rail: rise,
     fade, unclip, in one stagger */
  $$('[data-catalog-rail], [data-colors-rail]').forEach((rail) => {
    if (armed.has(rail)) return;
    armed.add(rail);
    teardown.push(inView(rail, () => {
      tween($$('[data-ctile]', rail), { y: [MO.riseMd, 0], opacity: [0, 1], clipPath: ['inset(0 0 12% 0)', 'inset(0 0 0% 0)'] },
        { duration: MO.deliberate, delay: stagger(MO.stagBase), ease: MO.eEntrance });
    }, { amount: 0.15 }));
  });
  /* the fabric plate settles like the hero's */
  const stage = $('[data-fabric-media]');
  if (stage && !armed.has(stage)) {
    armed.add(stage);
    teardown.push(inView(stage, () => { tween(stage, { scale: [MO.scaleIn, 1] }, { duration: MO.cinematic, ease: MO.eEntrance }); }, { margin: '0px 0px -10% 0px' }));
  }
  /* once a section has started revealing, later re-renders just show */
  $$('section').forEach((s) => {
    if (armed.has(s)) return;
    armed.add(s);
    teardown.push(inView(s, () => { s.setAttribute('data-revealed', ''); }, { margin: '0px 0px -10% 0px' }));
  });
}

/* ============================================================
   LAB BAR — hidden on templates; H shows it
   ============================================================ */
function initLabBar() {
  const labBar = $('[data-lab-bar]');
  const handle = $('[data-lab-handle]');
  const slider = $('#lab-speed');
  const readout = $('[data-readout]');
  slider.addEventListener('input', () => {
    const v = parseFloat(slider.value);
    readout.textContent = `${v.toFixed(2)}x`;
    setSpeed(v);
  });
  const rebuild = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    teardown.splice(0).forEach((fn) => { try { fn?.(); } catch (e) { /* ignore */ } });
    clearTracked();
    $$('section').forEach((s) => s.removeAttribute('data-revealed'));
    const pre = $$('[data-reveal], [data-reveal-lines], [data-reveal-plate], [data-vp], [data-ctile], [data-core-item], [data-core-reveal], [data-fabric-reveal], [data-dyu] [data-slot], [data-footer-item], [data-hero-eyebrow], [data-hero-title], [data-hero-body]');
    /* hide through motion, so its cached opacity agrees: a direct style
       write left Reduced's instant reveal (opacity already "1") with nothing
       to change, and revealed sections stayed blank after a Replay */
    pre.forEach((el) => { animate(el, { opacity: 0 }, { duration: 0 }); armed.delete(el); });
    $$('section').forEach((s) => armed.delete(s));
    armed.delete($('[data-fabric-media]'));
    armed.delete($('[data-catalog-rail]'));
    armed.delete($('[data-colors-rail]'));
    requestAnimationFrame(boot);
  };
  $('[data-replay]').addEventListener('click', rebuild);
  const rb = $('[data-reduced-btn]');
  rb.setAttribute('aria-pressed', String(state.reduced));
  rb.addEventListener('click', () => {
    state.reduced = !state.reduced;
    rb.setAttribute('aria-pressed', String(state.reduced));
    fabricSync();
    rebuild();
  });
  const gb = $('[data-grid-btn]');
  const ov = $('[data-grid-overlay]');
  gb.addEventListener('click', () => {
    const on = gb.getAttribute('aria-pressed') !== 'true';
    gb.setAttribute('aria-pressed', String(on));
    ov.dataset.on = String(on);
  });
  let killed = false;
  $('[data-hide-btn]').addEventListener('click', () => { killed = true; handle.hidden = true; labBar.remove(); });
  addEventListener('keydown', (e) => {
    if (killed) return;
    if (e.key.toLowerCase() !== 'h' || e.metaKey || e.ctrlKey || e.altKey) return;
    if (/^(input|textarea|select)$/i.test(e.target.tagName) || e.target.isContentEditable) return;
    const hide = !labBar.hidden;
    labBar.hidden = hide;
    handle.hidden = !hide;
  });
  handle.addEventListener('click', () => { if (killed) return; labBar.hidden = false; handle.hidden = true; });
}

/* ============================================================
   BOOT
   ============================================================ */
function initOnce() {
  mountToggles();
  initPromo();
  initHeroScroll();
  initCatalog();
  initCore();
  initFabric();
  initColors();
  initDyu();
  initCampaign();
  initValueProps();
  /* the Core Line and the uniform table re-render on a gender change made
     elsewhere — keep their new content visible once they've revealed */
  onGender((g, source) => {
    requestAnimationFrame(() => {
      if (source !== $('[data-core]')) settle($('[data-core]'), '[data-core-item], [data-core-reveal]');
      if (source !== $('[data-dyu]')) settle($('[data-dyu]'), '[data-slot]');
    });
  });
}

/* Text Link [Primary] hover — the Homepage / PDP / PLP [data-hover-link]:
   lift 2px and settle to 82% over quick, on the entrance curve, and back */
function initHoverLinks() {
  $$('[data-hover-link]').forEach((l) => {
    teardown.push(hover(l, () => {
      track(animate(l, { y: -2, opacity: 0.82 }, { duration: MO.quick, ease: MO.eEntrance }));
      return () => track(animate(l, { y: 0, opacity: 1 }, { duration: MO.quick, ease: MO.eEntrance }));
    }));
  });
}

function boot() {
  document.documentElement.dataset.reduced = String(state.reduced);
  initIntro();
  armReveals();
  initHoverLinks();
}

/* the value props: desktop hover and focus reveal each logline (CSS); a
   tap or click toggles it at both widths */
function initValueProps() {
  /* a tap (mobile, with the plus / minus) or a click (desktop) opens one
     logline at a time; tapping it again closes it */
  $$('[data-vp-toggle]').forEach((b) => b.addEventListener('click', () => {
    const on = b.getAttribute('aria-expanded') !== 'true';
    $$('[data-vp-toggle]').forEach((x) => x.setAttribute('aria-expanded', String(x === b && on)));
  }));
}

initOnce();
boot();
document.documentElement.setAttribute('data-mo-ready', '');
initLabBar();

addEventListener('load', () => {
  const broken = $$('img').filter((i) => i.getAttribute('src') && i.complete && i.naturalWidth === 0);
  if (broken.length) console.error(`[assets] ${broken.length} image(s) failed to load`, broken.map((i) => i.src));
  else console.info(`[assets] ${$$('img').length} images OK`);
});
console.info('%cFIGS Motion Lab · 18 New Customer Landing · V0 (archive)', 'font-weight:700',
  '\nData:', Object.fromEntries(Object.entries(DATA).map(([g, d]) => [g, d.ready ? 'wearfigs.com export' : 'Figma only'])),
  '\nReduced:', state.reduced);
